import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Typography from '@material-ui/core/Typography';
import { createStyles, IconButton, makeStyles, Theme } from '@material-ui/core';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { baseTheme } from '../../../theme';
import { AddCircle, RemoveCircle } from '@material-ui/icons';
import { followLeaderGroup, loadGroupPosts, unFollowLeaderGroup } from '../../../store';
import GroupPostList from '../group-posts-list';
import { userCanAccess } from '../../../services';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
      paddingLeft: theme.spacing(0),
      paddingRigth: theme.spacing(0),
      marginTop: theme.spacing(0),
      marginBottom: theme.spacing(0),
      display: 'flex',
      alignItems: 'center',
    },
    accordion: {
      width: '100%',
      '&$expanded': {
        marginBottom: '0 !important;',
      },
      '&Mui-expanded': {
        minHeight: 0,
      },
    },
    accordionSummary: {
      alignSelf: 'flex-start',
      marginTop: 0,
      '&$expanded': {
        marginBottom: '0 !important;',
      },
      '&Mui-expanded': {
        minHeight: 0,
      },
      '&MuiAccordianSummary-content.Mui-expanded': {
        margin: 'auto',
      },
    },
    text: {
      fontWeight: 'bold',
      fontSize: '1.4em',
    },
    public: {
      color: theme.palette.primary.main,
      borderTop: `solid 1px ${theme.palette.primary.main}`,
      borderBottom: `solid 1px ${theme.palette.primary.main}`,
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
    },
    private: {
      color: theme.palette.secondary.main,
      borderTop: `solid 1px ${theme.palette.secondary.main}`,
      borderBottom: `solid 1px ${theme.palette.secondary.main}`,
      '&:hover': {
        backgroundColor: theme.palette.secondary.light,
      },
    },
    publicText: {
      color: theme.palette.primary.main,
    },
    privateText: {
      color: theme.palette.secondary.main,
    },
    button: {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.background.paper,
      borderRadius: '5px',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      '&:hover': {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.primary.dark,
      },
    },
    alignRight: {
      textAlign: 'right',
    },
    expandIconClosed: {
      color: theme.palette.secondary.main,
    },
  }),
);

export default function GroupItems(props: any) {
  const { id, name, type, leader } = props;
  const classes = useStyles(baseTheme);
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.auth.currUser);
  const [allowedAccess, setAllowedAccess] = useState(false);

  const currFollowing = useSelector((state: any) => state.leaders.follow)
    .filter((item: any) => item.leader.id === leader.id);

  const isClosed = type.toLowerCase() === 'closed';
  const followingGroup = currFollowing[0]?.groups.includes(id)
    || currFollowing[0]?.groups
      .filter((group: any) => group.id === id).length;
  const addGroups = currFollowing[0]?.groups
    ? currFollowing[0].groups.map((group: any) => ({ id: group.id }))
    : [];
  const generateFollowData = {
    leader,
    groups: [
      ...addGroups,
      { id },
    ],
  };
  useEffect(() => {
    evaluateAccess();
  }, [currFollowing]);

  const removeGroup =
    currFollowing[0]?.groups?.filter((group: any) => group.id !== id) || [];
  const generateUnFollowData = {
    leader,
    groups: [
      ...removeGroup,
    ],
  };

  const generateMemberData = (status: string) => ({
    status,
    type: 'follower',
    uid: user.uid,
    displayName: user.displayName,
    email: user.email,
  });

  const handleAddPublicGroup = (event: any) => {
    event.stopPropagation();
    followLeaderGroup(
      followingGroup,
      generateMemberData('approved'),
      id,
      generateFollowData,
      dispatch,
    );
  };

  const handleAddClosedGroup = (event: any) => {
    event?.stopPropagation();
    followLeaderGroup(
      followingGroup,
      generateMemberData('unapproved'),
      id,
      generateFollowData,
      dispatch,
    );
  };

  const handleUnFollowGroup = async (event: any) => {
    event.stopPropagation();
    await unFollowLeaderGroup(
      generateMemberData('unapproved'),
      id,
      generateUnFollowData,
      dispatch,
    );
  };

  const onExpanded = (id: any) => async () => {
    if (followingGroup) {
      loadGroupPosts(leader.id, id, user, dispatch);
    }
  };
  const evaluateAccess = async () => {
    if (followingGroup) {
      setAllowedAccess(await userCanAccess(id, user));
    }
  };

  const renderFollowActions = isClosed
    ? <Button className={classes.button} onClick={handleAddClosedGroup}>Join</Button>
    : (<IconButton  color={isClosed ? 'secondary' : 'primary'}  onClick={handleAddPublicGroup}>
      <AddCircle  color={isClosed ? 'secondary' : 'primary'}  />
    </IconButton>);

  const renderUnfollowAction = (<IconButton onClick={handleUnFollowGroup}>
    <RemoveCircle color={isClosed ? 'secondary' : 'primary'} />
  </IconButton>);

  const renderAction = followingGroup
    ? renderUnfollowAction
    : renderFollowActions;

  const renderInstructions = () => {
    return (<>
      <Grid item>Please Join this group to view post content.</Grid>
      <Grid>{renderFollowActions}</Grid>
    </>);
  };
  const renderJoinStatus = (!followingGroup || !isClosed || allowedAccess)
  ? null
  : (
     <Typography variant="body2" className={`${classes.text} ${classes.privateText}`}>
        Pending approval
      </Typography>
    );

  return (
    <Grid
      item
      container
      className={`${classes.root} ${isClosed ? classes.private : classes.public}`}>
      <Accordion
        className={classes.accordion}
        TransitionProps={{ unmountOnExit: true }}
        onChange={onExpanded(id)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={isClosed ? classes.expandIconClosed : ''} />}
          aria-controls="Leader Groups"
          id="leaderId-header"
          className={classes.accordionSummary}
        >
          <Grid xs={9}>
            <Typography variant="body2" className={`${classes.text} ${isClosed ? classes.privateText : classes.publicText}`}>
              {name}
            </Typography>
          </Grid>
          <Grid container direction="row" xs={3} className={classes.alignRight}>
            <Grid item xs={9} container direction="row" alignItems="center" justify="flex-end">
              {renderJoinStatus}
            </Grid>
            <Grid item xs={3}>
              {renderAction}
            </Grid>
          </Grid>
        </AccordionSummary>
        <Grid item container direction="column">
          {followingGroup
            ? <GroupPostList
                  id={id}
                  leader={leader}
                  groupType={type}
                  allowedAccess={allowedAccess}
              />
            : renderInstructions()}
        </Grid>
      </Accordion>

    </Grid>
  );
}
